import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { sanitizeUsername, getUsernameError } from '@/lib/usernameValidation';
import { Warning, Check, X, Globe } from '@phosphor-icons/react';

const monoFont = "'IBM Plex Mono', monospace";
const fg = 'rgba(247,246,242,0.85)';
const borderBase = 'rgba(247,246,242,0.15)';

export function AdminSubdomainChange() {
  const { session } = useAuth();
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!session) return;
    supabase
      .from('user_profiles')
      .select('username')
      .eq('user_id', session.user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setCurrentUsername(data.username);
      });
  }, [session]);

  const validationError = getUsernameError(input);
  const isSameAsCurrent = input === currentUsername;

  const checkAvailability = useCallback(async (username: string) => {
    if (!session) return;
    setChecking(true);
    const { data } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('username', username)
      .neq('user_id', session.user.id)
      .maybeSingle();
    setAvailable(!data);
    setChecking(false);
  }, [session]);

  const handleInputChange = (raw: string) => {
    const sanitized = sanitizeUsername(raw);
    setInput(sanitized);
    setError(null);
    setAvailable(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const err = getUsernameError(sanitized);
    if (err || !sanitized || sanitized === currentUsername) return;

    debounceRef.current = setTimeout(() => {
      checkAvailability(sanitized);
    }, 400);
  };

  const handleConfirm = async () => {
    if (!session || !input || validationError || !available || isSameAsCurrent) return;
    setSaving(true);
    setError(null);

    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({ username: input, updated_at: new Date().toISOString() })
      .eq('user_id', session.user.id);

    if (updateError) {
      if (updateError.message?.includes('unique') || updateError.message?.includes('duplicate')) {
        setError('Este subdomínio foi registrado por outro usuário. Tente outro.');
        setAvailable(false);
      } else {
        setError(`Erro ao atualizar: ${updateError.message}`);
      }
      setSaving(false);
      return;
    }

    // Redirect to new subdomain
    window.location.href = `https://${input}.criaaa.com/admin`;
  };

  const canConfirm = input.length >= 3 && !validationError && available === true && !isSameAsCurrent && !saving;

  if (!currentUsername) return null;

  return (
    <div className="mb-8">
      <h3
        className="text-[11px] uppercase tracking-wider mb-4"
        style={{ fontFamily: monoFont, color: 'rgba(247,246,242,0.45)' }}
      >
        Seu endereço na Criaaa
      </h3>

      <div className="flex flex-col gap-4">
        {/* Current address display */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 14px',
            border: `1px solid ${borderBase}`,
            fontFamily: monoFont,
            fontSize: 13,
            fontWeight: 300,
            color: fg,
          }}
        >
          <Globe size={14} weight="thin" style={{ color: 'rgba(247,246,242,0.4)', flexShrink: 0 }} />
          <span>{currentUsername}.criaaa.com</span>
        </div>

        {!editing ? (
          <button
            onClick={() => { setEditing(true); setInput(''); setAvailable(null); setError(null); }}
            style={{
              fontFamily: monoFont,
              fontSize: 12,
              fontWeight: 300,
              textTransform: 'uppercase',
              padding: '8px 16px',
              border: `1px solid ${borderBase}`,
              backgroundColor: 'transparent',
              color: fg,
              cursor: 'pointer',
              alignSelf: 'flex-start',
            }}
          >
            Alterar subdomínio
          </button>
        ) : (
          <div className="flex flex-col gap-3">
            {/* Input */}
            <div style={{ position: 'relative' }}>
              <input
                value={input}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder="novo-subdominio"
                maxLength={20}
                autoFocus
                style={{
                  fontFamily: monoFont,
                  fontSize: 13,
                  fontWeight: 300,
                  padding: '8px 12px',
                  paddingRight: 140,
                  border: `1px solid ${borderBase}`,
                  backgroundColor: 'transparent',
                  color: fg,
                  width: '100%',
                  outline: 'none',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontFamily: monoFont,
                  fontSize: 12,
                  color: 'rgba(247,246,242,0.3)',
                  pointerEvents: 'none',
                }}
              >
                .criaaa.com
              </span>
            </div>

            {/* Validation feedback */}
            {input.length > 0 && (
              <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300 }}>
                {validationError ? (
                  <span style={{ color: '#ff6b6b' }}>{validationError}</span>
                ) : isSameAsCurrent ? (
                  <span style={{ color: 'rgba(247,246,242,0.35)' }}>Mesmo endereço atual</span>
                ) : checking ? (
                  <span style={{ color: 'rgba(247,246,242,0.35)' }}>Verificando...</span>
                ) : available === true ? (
                  <span style={{ color: '#00E87A', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Check size={12} weight="thin" /> Disponível
                  </span>
                ) : available === false ? (
                  <span style={{ color: '#ff6b6b', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <X size={12} weight="thin" /> Já está em uso
                  </span>
                ) : null}
              </div>
            )}

            {/* Warning */}
            <div
              style={{
                display: 'flex',
                gap: 10,
                padding: '12px 14px',
                backgroundColor: 'rgba(245,158,11,0.08)',
                border: '0.5px solid rgba(245,158,11,0.2)',
                fontFamily: monoFont,
                fontSize: 12,
                fontWeight: 300,
                color: 'rgba(245,158,11,0.85)',
                lineHeight: 1.5,
              }}
            >
              <Warning size={14} weight="thin" style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                Ao alterar seu subdomínio, o endereço atual ({currentUsername}.criaaa.com) deixará de funcionar imediatamente.
                Atualize todos os links que compartilhou.
              </span>
            </div>

            {/* Error */}
            {error && (
              <p style={{ fontFamily: monoFont, fontSize: 12, color: '#FF3F2D' }}>{error}</p>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleConfirm}
                disabled={!canConfirm}
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  fontWeight: 300,
                  textTransform: 'uppercase',
                  padding: '8px 20px',
                  border: `1px solid ${fg}`,
                  backgroundColor: canConfirm ? 'rgba(247,246,242,0.85)' : 'transparent',
                  color: canConfirm ? '#0A0A0A' : fg,
                  cursor: canConfirm ? 'pointer' : 'default',
                  opacity: canConfirm ? 1 : 0.3,
                  transition: 'all 0.15s ease',
                }}
              >
                {saving ? 'Salvando...' : 'Confirmar Alteração'}
              </button>
              <button
                onClick={() => { setEditing(false); setInput(''); setAvailable(null); setError(null); }}
                disabled={saving}
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  fontWeight: 300,
                  textTransform: 'uppercase',
                  padding: '8px 16px',
                  border: `1px solid ${borderBase}`,
                  backgroundColor: 'transparent',
                  color: 'rgba(247,246,242,0.5)',
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
